import { useEffect, useMemo, useState } from 'preact/hooks';
import { Link, goBack, navigate, useRoute, type RouteState } from '../lib/router';
import { BOOKS_CHANGED } from '../lib/changed';
import { useFlag } from '../shell/hooks';
import { useNotice } from '../shell/notice';
import { PhoneSearch } from '../shell/Bars';
import { trailSection, trailState } from '../shell/sections';
import { BackLink } from '../ui/BackLink';
import { ListRow, ListTable, type Col } from '../ui/ListTable';
import { SectionFold, SectionFoldAll } from '../ui/SectionFold';
import { SettingMenu } from '../ui/PillMenu';
import { ToTop } from '../ui/ToTop';
import { Cover, WorthRing } from '../ui/bits';
import { useTuck } from '../ui/useTuck';
import { bookLabel } from '../book/model';
import { BookSheet, type BookSheetKind } from '../queue/BookSheet';
import { MoveMenu, RowBook } from '../queue/parts';
import { copyText } from '../queue/model';
import { seriesOrder } from '../queue/actions';
import {
  PEOPLE_SORTS,
  authorGroups,
  countsLine,
  defaultSort,
  missingText,
  nextInSeries,
  nextLine,
  outOfOrder,
  outOfOrderLine,
  outOfOrderNote,
  placeText,
  seriesBooks,
  shelfText,
  sortBooks,
  type BookGroup,
  type PageBook,
  type PeopleSort,
  type PeopleSortKey,
} from './people';
import { PageNotFound, loadAuthorPage, loadSeriesPage, type AuthorPageData, type SeriesPageData } from './peopleLoad';
import './people.css';

// An author's page and a series' page (Discover, Part 2, v1.0: only the books on your list; boards
// 13, 13p, 17 and 18, agreed with Kristi Nov 3), behind the discover flag. Shared pages, like the
// book page: ← Back names where you came from, and the section bar keeps the section the trail
// began in. On a laptop a book opens in the side panel over the page.

type Loaded = { kind: 'loading' } | { kind: 'off' } | { kind: 'missing' } | { kind: 'error'; message: string } | { kind: 'ready'; data: AuthorPageData | SeriesPageData };

/** Each page's own route state, so it keeps its ← Back while a book's side panel is open over it. */
const ownState = new Map<string, RouteState>();

export function PeoplePage({ kind, id, state: routeState, wide, under }: { kind: 'author' | 'series'; id: string; state: RouteState; wide: boolean; under?: boolean }) {
  const on = useFlag('discover');
  const path = `/${kind}/${id}`;
  if (!under) ownState.set(path, routeState);
  const state = under ? (ownState.get(path) ?? {}) : routeState;
  const [loaded, setLoaded] = useState<Loaded>({ kind: 'loading' });
  const tucked = useTuck(wide);

  useEffect(() => {
    if (on === null) return;
    if (!on) return setLoaded({ kind: 'off' });
    let live = true;
    const load = (quiet: boolean) => {
      if (!quiet) setLoaded({ kind: 'loading' });
      (kind === 'author' ? loadAuthorPage(id) : loadSeriesPage(id))
        .then((data) => live && setLoaded({ kind: 'ready', data }))
        .catch((e: unknown) => live && setLoaded(e instanceof PageNotFound ? { kind: 'missing' } : { kind: 'error', message: e instanceof Error ? e.message : String(e) }));
    };
    load(false);
    // A move from a row, a sheet or the side panel: read the books again, in place.
    const again = () => load(true);
    window.addEventListener(BOOKS_CHANGED, again);
    return () => {
      live = false;
      window.removeEventListener(BOOKS_CHANGED, again);
    };
  }, [kind, id, on]);

  const section = trailSection(state);
  const back = <BackLink where={state.fromName ?? section.name} onClick={() => goBack(section.path)} />;
  const name = loaded.kind === 'ready' ? loaded.data.name : '';
  const body =
    loaded.kind === 'ready' ? (
      loaded.data.kind === 'author' ? (
        <AuthorBody data={loaded.data} state={state} wide={wide} />
      ) : (
        <SeriesBody data={loaded.data} state={state} wide={wide} />
      )
    ) : loaded.kind === 'loading' || on === null ? (
      <p class="shelves-quiet" aria-busy="true">
        Loading…
      </p>
    ) : loaded.kind === 'off' ? (
      <div class="heads-up">This page isn’t available.</div>
    ) : loaded.kind === 'missing' ? (
      <div class="heads-up">There’s no such {kind === 'author' ? 'author' : 'series'} in the catalog.</div>
    ) : (
      <div class="heads-up">The page couldn’t be loaded: {loaded.message}</div>
    );

  const head = (
    <header class="pp-head">
      <p class="bk-label">{kind === 'author' ? 'Author' : 'Series'}</p>
      <h1 class="book-title pp-name">{name || ' '}</h1>
      {loaded.kind === 'ready' && loaded.data.kind === 'series' && loaded.data.author ? (
        <p class="pp-by">
          <Link href={`/author/${loaded.data.author.id}`} state={trailState(path, state, loaded.data.name)}>
            by {loaded.data.author.name}
          </Link>
        </p>
      ) : null}
      {loaded.kind === 'ready' ? <p class="pp-counts">{countsLine(loaded.data.books)}</p> : null}
    </header>
  );

  if (wide) {
    return (
      <div class="page pp-page">
        <div class="pp-back">{back}</div>
        {head}
        <div class="pp-bar">
          <SectionFoldAll />
          <ToTop wide={wide} />
        </div>
        {body}
      </div>
    );
  }
  return (
    <div class={`book-phone pp-phone${tucked ? ' tucked' : ''}`}>
      <header class="ptop book-phone-bar">
        {back}
        <PhoneSearch />
      </header>
      <div class="page pp-page">
        {head}
        <div class="pp-bar">
          <SectionFoldAll />
        </div>
        {body}
        <ToTop wide={wide} float />
      </div>
    </div>
  );
}

/** Opening a book from the page: the side panel over it on a laptop, the book page on a phone. */
function useOpen(state: RouteState, name: string) {
  const { path } = useRoute();
  return (b: PageBook) => () => navigate(`/book/${bookLabel(b.label)}`, { state: trailState(path, state, name), keepScroll: true });
}

/** The page's sorts: each table its own on a laptop (by its headers); on a phone, one Sort pill for every group. */
function useSorts() {
  const [sorts, setSorts] = useState<Record<string, PeopleSort>>({});
  const [all, setAll] = useState<PeopleSortKey | null>(null);
  const sortOf = (g: Pick<BookGroup, 'key' | 'kind'>): PeopleSort => sorts[g.key] ?? (all && all !== 'num' ? { key: all, desc: all === 'published' } : defaultSort(g));
  const flip = (g: Pick<BookGroup, 'key' | 'kind'>) => (k: PeopleSortKey) => {
    const now = sortOf(g);
    setSorts({ ...sorts, [g.key]: now.key === k ? { key: k, desc: !now.desc } : { key: k, desc: k === 'published' && g.kind !== 'series' } });
  };
  const pill = (
    <SettingMenu<PeopleSortKey>
      name="Sort"
      choices={PEOPLE_SORTS}
      value={all ?? 'num'}
      onChange={(v) => {
        setSorts({});
        setAll(v);
      }}
    />
  );
  return { sortOf, flip, pill, custom: Boolean(all && all !== 'num') };
}

function AuthorBody({ data, state, wide }: { data: AuthorPageData; state: RouteState; wide: boolean }) {
  const { path } = useRoute();
  const groups = useMemo(() => authorGroups(data.books), [data]);
  const open = useOpen(state, data.name);
  const sorts = useSorts();
  if (!groups.length) return <div class="heads-up">None of {data.name}’s books are on your list.</div>;
  return (
    <>
      {!wide ? (
        <div class="pp-sortline">
          <span class="lt-order">In each group: series order (newest first outside a series)</span>
          {sorts.pill}
        </div>
      ) : null}
      {groups.map((g) => {
        const odd = g.kind === 'series' ? outOfOrder(g.books) : null;
        const seriesLink = g.seriesId ? (
          <Link href={`/series/${g.seriesId}`} state={trailState(path, state, data.name)} class="pp-more">
            See the whole series ›
          </Link>
        ) : null;
        const sort = sorts.sortOf(g);
        return (
          <SectionFold key={g.key} store={`people:${data.id}`} id={g.key} title={g.title} count={g.books.length} noun={['book on your list', 'books on your list']} end={wide ? seriesLink : undefined}>
            {odd ? (
              <p class="pp-notice">
                <WorthRing tip="Out of series order in Future reads" /> {outOfOrderLine(odd)}{' '}
                <Link href={`/series/${g.seriesId}`} state={trailState(path, state, data.name)}>
                  Put them in series order on the series page ›
                </Link>
              </p>
            ) : null}
            <BooksTable
              label={g.title}
              books={sortBooks(g.books, sort)}
              numbered={g.kind === 'series'}
              sort={sort}
              onSort={sorts.flip(g)}
              order={g.kind === 'series' ? 'In series order · sort by any header' : 'Newest first · sort by any header'}
              wide={wide}
              open={open}
            />
            {g.kind === 'series' ? <Missing books={g.books} /> : null}
            {!wide ? seriesLink : null}
          </SectionFold>
        );
      })}
    </>
  );
}

function Missing({ books }: { books: PageBook[] }) {
  const text = missingText(books.map((b) => b.seriesPos));
  return text ? <p class="pp-missing">{text}</p> : null;
}

function SeriesBody({ data, state, wide }: { data: SeriesPageData; state: RouteState; wide: boolean }) {
  const { path } = useRoute();
  const notice = useNotice();
  const open = useOpen(state, data.name);
  const sorts = useSorts();
  const books = useMemo(() => seriesBooks(data.books), [data]);
  const next = nextInSeries(books);
  const odd = outOfOrder(books);
  const [sheet, setSheet] = useState<{ b: PageBook; what: BookSheetKind } | null>(null);
  const [busy, setBusy] = useState(false);
  const group = { key: 'books', kind: 'series' as const };
  const sort = sorts.sortOf(group);
  if (!books.length) return <div class="heads-up">None of the books in {data.name} are on your list.</div>;
  const afterDetails = next ? data.details.get(next.after.id) : undefined;
  return (
    <>
      {next ? (
        <section class="pp-next" aria-label="Next in the series for you">
          <Cover title={next.book.title} url={next.book.cover} size="sm" />
          <div class="pp-next-text">
            <p class="bk-label">Next in the series for you</p>
            <button type="button" class="pp-next-title" onClick={open(next.book)}>
              {next.book.title}
            </button>
            <p class="pp-next-line">{nextLine(next, afterDetails?.reading?.percent)}</p>
            {afterDetails?.note ? (
              <p class="pp-next-note">
                Your note on {next.after.title}: “{afterDetails.note}”
              </p>
            ) : null}
          </div>
          {next.book.status === 'future_reads' || next.book.status === null ? (
            <button type="button" class="btn-secondary pp-next-move" onClick={() => setSheet({ b: next.book, what: { kind: 'place', to: 'up_next' } })}>
              Move to Up Next
            </button>
          ) : null}
        </section>
      ) : null}
      {odd ? (
        <SectionFold store={`people:${data.id}`} id="order" title="Out of series order in Future reads" worth="Out of series order in Future reads">
          <p class="pp-note">{outOfOrderNote(odd)}</p>
          <ListTable
            label="Your Future reads sequence"
            class="pp-places"
            order="In your Future reads sequence"
            cols={[
              { label: 'Place', width: '72px', num: true },
              { label: 'Book', width: 'minmax(0, 1fr)', book: true },
            ]}
          >
            {odd.books.map((b) => (
              <ListRow
                key={b.id}
                cells={[`#${b.rank}`, <BookLine b={b} />]}
                phone={{ lead: <span class="pp-place">#{b.rank}</span>, book: <BookLine b={b} /> }}
              />
            ))}
          </ListTable>
          <button
            type="button"
            class="btn-main pp-order"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void seriesOrder(notice, { id: data.id, name: data.name }).finally(() => setBusy(false));
            }}
          >
            Put them in series order
          </button>
        </SectionFold>
      ) : null}
      <SectionFold store={`people:${data.id}`} id="books" title="The books" count={books.length} noun={['book on your list', 'books on your list']}>
        {!wide ? <div class="pp-sortline">{sorts.pill}</div> : null}
        <BooksTable label={data.name} books={sortBooks(books, sort)} numbered sort={sort} onSort={sorts.flip(group)} order="In series order (#) · sort by any header" wide={wide} open={open} onSheet={(b, what) => setSheet({ b, what })} />
        <Missing books={books} />
      </SectionFold>
      {data.author ? (
        <SectionFold store={`people:${data.id}`} id="more" title="More">
          <p class="pp-more-line">
            <Link href={`/author/${data.author.id}`} state={trailState(path, state, data.name)} class="pp-more">
              See more by {data.author.name} on their page ›
            </Link>
          </p>
        </SectionFold>
      ) : null}
      {sheet ? <BookSheet label={sheet.b.label} title={sheet.b.title} what={sheet.what} onClose={() => setSheet(null)} /> : null}
    </>
  );
}

/** "The Bat · Book 1", in the out-of-order list. */
function BookLine({ b }: { b: PageBook }) {
  return (
    <span class="pp-bookline">
      <span class="lt-title">{b.title}</span>
      {placeText(b) ? <span class="pp-quiet"> · {placeText(b)}</span> : null}
    </span>
  );
}

/**
 * The one books table of these pages (the shared ListTable): # · Book · Published · Shelf · Copy ·
 * Your read · Move ▾. On a phone each book is a block: the book and its year, Move, then the shelf,
 * copy and read as one grey line.
 */
function BooksTable({
  label,
  books,
  numbered,
  sort,
  onSort,
  order,
  wide,
  open,
  onSheet,
}: {
  label: string;
  books: PageBook[];
  numbered: boolean;
  sort: PeopleSort;
  onSort: (k: PeopleSortKey) => void;
  order: string;
  wide: boolean;
  open: (b: PageBook) => () => void;
  onSheet?: (b: PageBook, what: BookSheetKind) => void;
}) {
  const editOn = useFlag('book_page.edit');
  const [sheet, setSheet] = useState<{ b: PageBook; what: BookSheetKind } | null>(null);
  const toSheet = onSheet ?? ((b: PageBook, what: BookSheetKind) => setSheet({ b, what }));
  const cols: Col<PeopleSortKey>[] = [
    ...(numbered ? [{ label: '#', width: '48px', sort: 'num' as const, num: true }] : []),
    { label: 'Book', width: 'minmax(0, 1.6fr)', sort: 'title', book: true },
    { label: 'Published', width: '96px', sort: 'published', num: true },
    { label: 'Shelf', width: 'minmax(0, 1fr)', sort: 'shelf' },
    { label: 'Copy', width: 'minmax(0, 1fr)', sort: 'copy' },
    { label: 'Your read', width: 'minmax(0, 1.2fr)', sort: 'read' },
    { width: '96px' },
  ];
  return (
    <>
      {wide ? <p class="lt-order">{order}</p> : null}
      <ListTable<PeopleSortKey> label={label} cols={cols} sort={sort} onSort={onSort} class="pp-table">
        {books.map((b) => {
          const copy = copyText(b) ?? 'Not owned yet';
          const move = <MoveMenu row={b} pastRead={Boolean(editOn)} onSheet={(what) => toSheet(b, what)} />;
          const n = numbered ? <span class="pp-num">{b.seriesLabel && b.seriesPos === null ? b.seriesLabel : b.seriesPos ?? ''}</span> : null;
          const book = <RowBook title={b.title} sub={wide ? '' : b.published ? String(b.published) : ''} cover={b.cover} onOpen={open(b)} />;
          return (
            <ListRow
              key={b.id}
              data-row={b.id}
              quiet={b.status === 'not_for_me'}
              cells={[...(n ? [n] : []), book, b.published ?? '', <span class="pill">{shelfText(b)}</span>, copy, b.read, move]}
              phone={{ lead: n ?? undefined, book, control: move, line: [shelfText(b), copy, b.read].filter(Boolean).join(' · ') }}
            />
          );
        })}
      </ListTable>
      {sheet ? <BookSheet label={sheet.b.label} title={sheet.b.title} what={sheet.what} onClose={() => setSheet(null)} /> : null}
    </>
  );
}
